import * as React from "react";
import { SearchIcon } from "lucide-react";

import { cn } from "../../lib/utils";

function Card({
	className,
	ref,
	...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) {
	return (
		<div
			ref={ref}
			className={cn(
				"rounded-lg border border-neutral-200 bg-card text-neutral-950 shadow-xs dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-50",
				className,
			)}
			{...props}
		/>
	);
}

function CardHeader({
	className,
	ref,
	...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) {
	return (
		<div
			ref={ref}
			className={cn("flex flex-col space-y-1.5 p-6", className)}
			{...props}
		/>
	);
}

function CardTitle({
	className,
	ref,
	...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) {
	return (
		<div
			ref={ref}
			className={cn(
				"text-2xl font-semibold leading-none tracking-tight",
				className,
			)}
			{...props}
		/>
	);
}

function CardDescription({
	className,
	ref,
	...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) {
	return (
		<div
			ref={ref}
			className={cn(
				"text-sm text-neutral-500 dark:text-neutral-400",
				className,
			)}
			{...props}
		/>
	);
}

function CardAction({
	className,
	ref,
	...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) {
	return (
		<div
			ref={ref}
			className={cn("self-start justify-self-end", className)}
			{...props}
		/>
	);
}

function CardContent({
	className,
	ref,
	...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) {
	return <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />;
}

function CardFooter({
	className,
	ref,
	...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) {
	return (
		<div
			ref={ref}
			className={cn("flex items-center p-6 pt-0", className)}
			{...props}
		/>
	);
}

/**
 * Props for the {@link CardEmpty} component.
 *
 * @property message - Primary message displayed when the card has no content.
 * @property hint - Optional secondary hint text rendered below the message.
 * @property icon - Optional icon rendered above the message; defaults to a search icon.
 */
type CardEmptyProps = React.HTMLAttributes<HTMLDivElement> & {
	message?: string;
	hint?: string;
	icon?: React.ReactNode;
	ref?: React.Ref<HTMLDivElement>;
};

function CardEmpty({
	className,
	message = "No results found",
	hint,
	icon,
	ref,
	...props
}: CardEmptyProps) {
	return (
		<div
			ref={ref}
			className={cn(
				"flex flex-col items-center justify-center py-12 px-6 text-center",
				className,
			)}
			{...props}
		>
			<div className="mb-3 text-neutral-400">
				{icon === undefined ? <SearchIcon className="h-8 w-8" /> : icon}
			</div>
			<p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
				{message}
			</p>
			{hint && (
				<p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
					{hint}
				</p>
			)}
		</div>
	);
}

export {
	Card,
	CardHeader,
	CardFooter,
	CardTitle,
	CardDescription,
	CardAction,
	CardContent,
	CardEmpty,
};
